import { useContext, useState } from "react";
import { Box, Typography } from "@mui/material";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import { useTranslation } from "react-i18next";
import { InfoCard, CustomButton, TextInput, SnackBar } from "src/design";
import { UserContext } from "src/Utils/Context/UserContext";
import { useUpdateOwnerProfile } from "src/data/account/owner";
import {
  colorBorder,
  colorTextPrimary,
  colorTextMuted,
} from "src/design/tokens";

const Field = ({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      py: 2,
      borderTop: `1px solid ${colorBorder}`,
    }}
  >
    <Box sx={{ color: colorTextMuted, display: "flex" }}>{icon}</Box>
    <Box>
      <Typography variant="body2" sx={{ color: colorTextMuted }}>
        {label}
      </Typography>
      <Typography sx={{ fontWeight: 600, color: colorTextPrimary }}>
        {value}
      </Typography>
    </Box>
  </Box>
);

export const AccountDetailsCard = () => {
  const { t } = useTranslation();
  const { user } = useContext(UserContext);

  const [editing, setEditing] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [toast, setToast] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const { mutate, isPending } = useUpdateOwnerProfile(
    () => {
      setEditing(false);
      setToast({
        open: true,
        message: t("configuration.account.updateSuccess"),
        severity: "success",
      });
    },
    () =>
      setToast({
        open: true,
        message: t("configuration.account.updateError"),
        severity: "error",
      }),
  );

  const startEditing = () => {
    setFirstName(user?.firstName ?? "");
    setLastName(user?.lastName ?? "");
    setPhone(user?.phone ?? "");
    setEditing(true);
  };

  const handleSave = () => mutate({ firstName, lastName, phone });

  return (
    <>
      <InfoCard
        title={t("configuration.account.title")}
        subtitle={t("configuration.account.subtitle")}
        icon={<PersonOutlineOutlinedIcon />}
        action={
          editing ? (
            <Box sx={{ display: "flex", gap: 1 }}>
              <CustomButton
                color="inverted"
                onClick={() => setEditing(false)}
                disabled={isPending}
              >
                {t("configuration.account.cancel")}
              </CustomButton>
              <CustomButton color="black" onClick={handleSave} disabled={isPending}>
                {t("configuration.account.save")}
              </CustomButton>
            </Box>
          ) : (
            <CustomButton color="inverted" onClick={startEditing}>
              {t("configuration.account.edit")}
            </CustomButton>
          )
        }
      >
        {editing ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <TextInput
              label={t("configuration.account.firstName")}
              value={firstName}
              onChange={setFirstName}
              fullwidth
            />
            <TextInput
              label={t("configuration.account.lastName")}
              value={lastName}
              onChange={setLastName}
              fullwidth
            />
            <TextInput
              label={t("configuration.account.phone")}
              value={phone}
              onChange={setPhone}
              fullwidth
            />
            <TextInput
              label={t("configuration.account.email")}
              value={user?.email ?? ""}
              onChange={() => {}}
              disabled
              fullwidth
            />
          </Box>
        ) : (
          <>
            <Field
              icon={<PersonOutlineOutlinedIcon fontSize="small" />}
              label={t("configuration.account.fullName")}
              value={`${user?.firstName ?? ""} ${user?.lastName ?? ""}`.trim()}
            />
            <Field
              icon={<MailOutlineIcon fontSize="small" />}
              label={t("configuration.account.email")}
              value={user?.email ?? ""}
            />
            <Field
              icon={<PhoneOutlinedIcon fontSize="small" />}
              label={t("configuration.account.phone")}
              value={user?.phone ?? ""}
            />
          </>
        )}
      </InfoCard>

      <SnackBar
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
      />
    </>
  );
};
